import { useApiQuery } from '../../lib/api.js';
import { useTranslation } from '../../lib/i18n.js';
import { listParam, toggleListValue } from '../../lib/list-sort.js';
import { personChips, usePeopleNames, type FilterChip } from '../../components/table-tools.js';
import { useCustomFields } from '../../components/custom-fields.js';

/** Every active work filter as a removable chip, with names instead of ids. */
export function useFilterChips(params: URLSearchParams, setParam: (key: string, value: string | null) => void): FilterChip[] {
  const { t } = useTranslation();
  const projects = useApiQuery('listProjects', { query: {} });
  const teams = useApiQuery('listTeams', { query: {} });
  const fields = useCustomFields();
  const names = usePeopleNames([...listParam(params, 'assigneeId'), ...listParam(params, 'ownerId'), ...listParam(params, 'createdById')]);
  const chips: FilterChip[] = [];
  const many = (param: string, role: string, label: (value: string) => string) => {
    for (const value of listParam(params, param)) {
      chips.push({
        key: `${param}:${value}`,
        label: (
          <>
            <span className="text-fg-muted">{role}:</span> {label(value)}
          </>
        ),
        onRemove: () => setParam(param, toggleListValue(params, param, value)),
      });
    }
  };
  const one = (param: string, label: string) => {
    if (params.get(param)) chips.push({ key: param, label, onRemove: () => setParam(param, null) });
  };
  many('phase', t('work.filters.phase'), (v) => t(`status.phase.${v}`));
  many('priority', t('work.filters.priority'), (v) => t(`status.priority.${v}`));
  many('type', t('work.filters.type'), (v) => t(`status.workType.${v}`));
  for (const [param, role, none] of [
    ['assigneeId', t('work.columns.assignee'), t('common.unassigned')],
    ['ownerId', t('work.columns.owner'), t('table.noOwner')],
    ['createdById', t('table.createdBy'), t('table.noOwner')],
  ] as const) {
    chips.push(...personChips(params, setParam, param, role, names, t('common.me'), none));
  }
  many('projectId', t('work.filters.project'), (v) => projects.data?.data.find((p) => p.id === v)?.name ?? '…');
  many('teamId', t('work.filters.team'), (v) => teams.data?.data.find((team) => team.id === v)?.name ?? '…');
  many('label', t('work.filters.label'), (v) => v);
  const due = params.get('due');
  if (due) one('due', `${t('work.filters.due')}: ${t(`table.due.${due}`)}`);
  const field = params.get('field');
  if (field) {
    const [fieldId, ...rest] = field.split(':');
    const definition = fields.find((f) => f.id === fieldId);
    const value = rest.join(':');
    const shown = !value ? t('table.empty') : value === 'true' ? t('common.yes') : value === 'false' ? t('common.no') : (definition?.options.find((o) => o.id === value)?.label ?? value);
    one('field', `${definition?.name ?? t('fields.title')}: ${shown}`);
  }
  if (params.get('blocked') === 'true') one('blocked', t('work.filters.blocked'));
  if (params.get('associationStatus') === 'PROVISIONAL') one('associationStatus', t('work.filters.provisional'));
  if (params.get('includeCanceled') === 'true') one('includeCanceled', t('work.filters.includeCanceled'));
  if (params.get('archived') === 'true') one('archived', t('work.filters.archived'));
  if (params.get('unverified') === 'true') one('unverified', t('noEvidence.filter'));
  const agentCreated = params.get('agentCreated');
  if (agentCreated === 'true' || agentCreated === 'false') one('agentCreated', t(agentCreated === 'true' ? 'agentCreated.filter' : 'agentCreated.filterPeople'));
  return chips;
}
